import { useState, useEffect, useRef } from 'react';
import useStyle from './index.styles';
import { Button, Steps, Form, Input, Row, Col, Select, Switch, Checkbox, UploadFile } from 'antd';
import { useTranslation } from 'react-i18next';
import { getAllCountries, getTrimmedString, isValidName } from '../../utils';
import Upload from '../../DLS/Upload';
import { FieldError } from 'rc-field-form/es/interface';
import { notificationMessage, useIsMobile } from '../../utils/hooks';
import { useMemorial } from '../../context/memorial.provider';
import useApi from '../../utils/api';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CaptchaMemorial from '../../DLS/CaptchaMemorial';
import SuccessMessage from '../SuccessMessage';
import TextAreaWithEmojiPicker from '../TextAreaWithEmojiPicker';

const countries = getAllCountries();

export const AddCommentForm = ({
  deceasedName,
  isMemorialCreator: isMemorialCreatorFromProps,
  resetValues,
  onClose,
}: {
  deceasedName?: string;
  isMemorialCreator?: boolean;
  resetValues?: boolean;
  onClose?: () => void;
}) => {
  const classes = useStyle();
  const { id: userId } = useMemorial();
  const [formPersonalInformation] = Form.useForm();
  const [formCommentAndImage] = Form.useForm();
  const [formPolicies] = Form.useForm();
  const [step, setStep] = useState<number>(0);
  const [showFormImage, setShowFormImage] = useState<boolean>(false);
  const [photograph, setPhotograph] = useState<UploadFile[]>([]);
  const [hasErrorInPhone, setHasErrorInPhone] = useState<boolean>(false);
  const [hasErrorsFormPersonalInformation, setHasErrorsFormPersonalInformation] =
    useState<boolean>(true);
  const [hasErrorsFormCommentAndImage, setHasErrorsFormCommentAndImage] = useState<boolean>(true);
  const [hasErrorsFormPolicies, setHasErrorsFormPolicies] = useState<boolean>(true);
  const [showErrorInPhotographOption, setShowErrorInPhotographOption] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [showRequiredErrorInCaptcha, setShowRequiredErrorInCaptcha] = useState<boolean>(false);

  const { i18n, t } = useTranslation();
  const isMobile = useIsMobile();
  const { api } = useApi();
  const URLParams = useParams();
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const deceasedNameToShow = deceasedName || searchParams.get('name');
  const isMemorialCreator =
    isMemorialCreatorFromProps || searchParams.get('isMemorialCreator') === 'true';
  const comment = Form.useWatch('comment', formCommentAndImage) || '';
  const imageFormContainer = useRef<HTMLDivElement>(null);
  const hasErrorPersonalInformation =
    step === 0 &&
    (hasErrorsFormPersonalInformation ||
      (showFormImage && (photograph.length === 0 || photograph[0].status !== 'done')));

  useEffect(() => {
    if (showFormImage) {
      imageFormContainer?.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }, [showFormImage]);

  useEffect(() => {
    if (resetValues) {
      formPersonalInformation.resetFields();
      formCommentAndImage.resetFields();
      formPolicies.resetFields();
      setStep(0);
      setShowFormImage(false);
      setPhotograph([]);
      setHasErrorInPhone(false);
      setHasErrorsFormPersonalInformation(true);
      setHasErrorsFormCommentAndImage(true);
      setHasErrorsFormPolicies(true);
      setShowErrorInPhotographOption(false);
      setShowSuccess(false);
      setCaptchaToken('');
      setShowRequiredErrorInCaptcha(false);
    }
  }, [formPersonalInformation, formCommentAndImage, formPolicies, resetValues]);

  const onFinishFormPersonalInformation = () => {
    if (!hasErrorPersonalInformation) {
      setStep((prev) => prev + 1);
    }
  };

  const onFinishFormCommentAndImage = () => {
    setStep((prev) => prev + 1);
  };

  const onFinishFormPolicies = () => {
    if (userId) {
      const { comment } = formCommentAndImage.getFieldsValue();
      const { check1, check4 } = formPolicies.getFieldsValue();
      const bodyFormData = new FormData();
      bodyFormData.append('message', getTrimmedString(comment));
      bodyFormData.append('notification_new_post', String(isMemorialCreator || !!check1));
      bodyFormData.append('notification_anual', String(isMemorialCreator || !!check4));
      bodyFormData.append('personal_information_id', String(URLParams.id));

      setSaving(true);
      api('multipart/form-data')
        .post('/book-memories', bodyFormData)
        .then((response) => {
          if (response.status === 200) {
            notificationMessage({
              message: t('addCommentModal.commentAdded'),
              type: 'success',
            });
            if (typeof onClose === 'function') {
              onClose();
            } else {
              navigate(-1);
            }
          }
        })
        .finally(() => setSaving(false));
    } else if (captchaToken) {
      const { firstName, lastName, email, phoneCode, phone } =
        formPersonalInformation.getFieldsValue();
      const { comment } = formCommentAndImage.getFieldsValue();
      const { check1, check4 } = formPolicies.getFieldsValue();
      const bodyFormData = new FormData();
      bodyFormData.append('personal_information_id', String(URLParams.id));
      bodyFormData.append('message', getTrimmedString(comment));
      bodyFormData.append('name', getTrimmedString(firstName));
      bodyFormData.append('last_name', getTrimmedString(lastName));
      bodyFormData.append('phone', getTrimmedString(phone));
      bodyFormData.append('code', phoneCode.split('-')[0]);
      bodyFormData.append('email', email);
      bodyFormData.append('notification_new_post', String(!!check1));
      bodyFormData.append('notification_anual', String(!!check4));
      bodyFormData.append('locale', i18n.language);

      if (photograph[0]?.originFileObj) {
        bodyFormData.append('photo', photograph[0].originFileObj);
      }

      setSaving(true);
      api('multipart/form-data')
        .post('/book-memories', bodyFormData)
        .then((response) => {
          if (response.status === 200) {
            setShowSuccess(true);
          }
        })
        .finally(() => setSaving(false));
    }
  };

  const checkValidFormPersonalInformation = () => {
    const { firstName, lastName, email, phoneCode, phone } =
      formPersonalInformation.getFieldsValue();
    const hasSomeFieldError =
      formPersonalInformation
        .getFieldsError()
        .map((e: FieldError) => e.errors)
        .flat().length !== 0;

    const someRequiredInputIsEmpty = !firstName || !lastName || !email || !phoneCode || !phone;

    const errorInPhone =
      formPersonalInformation
        .getFieldsError()
        .filter((e) => e.name[0] === 'phone' || e.name[0] === 'phoneCode')
        .map((e: FieldError) => e.errors)
        .flat().length !== 0;

    setHasErrorInPhone(errorInPhone);
    setHasErrorsFormPersonalInformation(hasSomeFieldError || someRequiredInputIsEmpty);
  };

  const checkValidFormCommentAndImage = () => {
    const hasSomeFieldError =
      formCommentAndImage
        .getFieldsError()
        .map((e: FieldError) => e.errors)
        .flat().length !== 0;

    setHasErrorsFormCommentAndImage(hasSomeFieldError);
  };

  const checkValidFormPolicies = () => {
    const { check2, check3 } = formPolicies.getFieldsValue();

    setHasErrorsFormPolicies(!check2 || !check3);
  };

  const onClickPrimaryButton = () => {
    if (isMemorialCreator) {
      onFinishFormPolicies();
    } else if (userId) {
      if (step === 0) {
        formCommentAndImage.submit();
      } else if (step === 1) {
        formPolicies.submit();
      }
    } else {
      if (step === 0) {
        setShowErrorInPhotographOption(true);
        formPersonalInformation.submit();
      } else if (step === 1) {
        formCommentAndImage.submit();
      } else if (step === 2) {
        !captchaToken && setShowRequiredErrorInCaptcha(true);
        formPolicies.submit();
      }
    }
  };

  const onChangeStepInWizard = (current: number) => {
    if (current < step) {
      setStep(current);
    } else {
      onClickPrimaryButton();
    }
  };

  const stepItems = [
    ...(userId
      ? []
      : [
          {
            title: isMobile ? (
              <div className="whitespace-nowrap">{t('addCommentModal.stepsMobile.0')}</div>
            ) : (
              t('addCommentModal.steps.0')
            ),
          },
        ]),
    {
      title: isMobile ? (
        <div className="relative left-[-8px]">{t('addCommentModal.stepsMobile.1')}</div>
      ) : (
        t('addCommentModal.steps.1')
      ),
    },
    { title: isMobile ? t('addCommentModal.stepsMobile.2') : t('addCommentModal.steps.2') },
  ];

  const getCommentForm = () => (
    <Form
      autoComplete="off"
      form={formCommentAndImage}
      onFinish={onFinishFormCommentAndImage}
      scrollToFirstError
      onFieldsChange={checkValidFormCommentAndImage}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <TextAreaWithEmojiPicker
            currentValue={comment}
            formItemLabel={t('addCommentModal.addComment')}
            formItemName="comment"
            maxLength={300}
            onChange={(value: string) => {
              formCommentAndImage.setFieldValue('comment', value);
              formCommentAndImage.validateFields(['comment']);
            }}
            placeholder={t('addCommentModal.form.comment.placeholder')}
            required
            rules={[
              {
                required: true,
                message: String(t('addCommentModal.form.comment.required')),
              },
              {
                validator: async (_obj, value) => {
                  if (value && value.trim().length === 0) {
                    return Promise.reject(
                      new Error(String(t('addCommentModal.form.comment.valid')))
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />
        </Col>
      </Row>
    </Form>
  );

  const getStepContent = () => {
    return (
      <>
        <div className={`${step !== 0 || userId ? 'hidden' : ''}`}>
          <Form
            autoComplete="off"
            form={formPersonalInformation}
            onFinish={onFinishFormPersonalInformation}
            scrollToFirstError
            onFieldsChange={checkValidFormPersonalInformation}
          >
            <Row gutter={[16, 0]}>
              <Col xs={12}>
                <Form.Item
                  required
                  name="firstName"
                  label={t('common.firstName')}
                  rules={[
                    { required: true, message: String(t('common.formsErrors.firstName.required')) },
                    {
                      validator: async (_obj, value) => {
                        if (value && !isValidName(value)) {
                          return Promise.reject(
                            new Error(String(t('common.formsErrors.firstName.valid')))
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder={String(t('register.placeholder.firstName'))} />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  required
                  name="lastName"
                  label={t('common.lastName')}
                  rules={[
                    { required: true, message: String(t('common.formsErrors.lastName.required')) },
                    {
                      validator: async (_obj, value) => {
                        if (value && !isValidName(value)) {
                          return Promise.reject(
                            new Error(String(t('common.formsErrors.lastName.valid')))
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder={String(t('register.placeholder.lastName'))} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  required
                  name="email"
                  label={t('common.email')}
                  rules={[
                    {
                      required: true,
                      message: String(t('common.formsErrors.email.required')),
                    },
                    {
                      type: 'email',
                      message: String(t('common.formsErrors.email.valid')),
                    },
                  ]}
                >
                  <Input placeholder={String(t('register.placeholder.email'))} />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  required
                  name="phoneCode"
                  label={t('common.code')}
                  rules={[
                    {
                      required: true,
                      message: String(t('register.formsErrors.countryCode.required')),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      !!String(option?.value)
                        .toLocaleLowerCase()
                        .includes(input.toLocaleLowerCase())
                    }
                    placeholder={String(t('register.placeholder.phoneCode'))}
                    notFoundContent={
                      <p className="px-[16px] py-[8px]">{t('common.withoutResults')}</p>
                    }
                  >
                    {countries.map((country) => (
                      <Select.Option
                        value={`${country.phoneCode}-${country.name}`}
                        key={`country-code-${country.isoCode}`}
                      >
                        <span className="mr-[4px]">{country.flag}</span>
                        <span className="mr-[4px]">{country.phoneCode}</span>
                        <span> {country.name}</span>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  required
                  name="phone"
                  label={t('common.phone')}
                  rules={[
                    {
                      required: true,
                      message: String(t('common.formsErrors.phone.required')),
                    },
                    {
                      validator: async (_obj, value) => {
                        if (value && !new RegExp('^[0-9]*$').test(value)) {
                          return Promise.reject(
                            new Error(String(t('common.formsErrors.phone.valid')))
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder={String(t('register.placeholder.phone'))} />
                </Form.Item>
              </Col>
              <p
                className={`smallParagraph relative mx-[8px] text-text2 ${
                  hasErrorInPhone ? 'top-[-10px]' : 'top-[-20px]'
                }`}
              >
                {t('register.phoneDescription')}
              </p>

              <Col xs={24} className="mt-[4px]">
                <div className="flex items-center">
                  <Switch
                    className="mr-[8px]"
                    onChange={setShowFormImage}
                    checked={showFormImage}
                  />
                  <p className="smallParagraphBold text-text2">
                    {t('addCommentModal.uploadPhotograph')}
                  </p>
                </div>
              </Col>
              {showFormImage && (
                <>
                  <Col xs={24} ref={imageFormContainer}>
                    <span
                      className={`${
                        showErrorInPhotographOption && photograph.length === 0
                          ? classes.uploadFileOptionWithError
                          : photograph.length === 0
                            ? classes.uploadFileOptionWithoutImage
                            : classes.uploadFileOptionWithImage
                      }`}
                    >
                      <Upload
                        accept="image/jpeg, image/png"
                        className="mt-[24px] min-w-[100%] [&>.ant-upload-select]:w-full"
                        maxCount={1}
                        fileList={photograph}
                        setFileList={setPhotograph}
                        maxFileSizeInMb={2}
                        showImageCrop
                      />
                      {showErrorInPhotographOption && photograph.length === 0 && (
                        <p className="smallParagraph text-danger">
                          {t('addCommentModal.form.photographFile.required')}
                        </p>
                      )}
                    </span>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        </div>
        <div className={`${(userId ? step !== 0 : step !== 1) ? 'hidden' : ''}`}>
          {getCommentForm()}
        </div>
        <div className={`${(userId ? step !== 1 : step !== 2) ? 'hidden' : ''}`}>
          <Form
            autoComplete="off"
            form={formPolicies}
            onFinish={onFinishFormPolicies}
            scrollToFirstError
            onFieldsChange={checkValidFormPolicies}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24}>
                <Form.Item
                  name="check1"
                  valuePropName="checked"
                  label={null}
                  className="check-input"
                >
                  <Checkbox>{t('addCommentModal.form.check1.label')}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  required
                  name="check2"
                  valuePropName="checked"
                  className="check-input"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(t('addCommentModal.form.check2.required')),
                    },
                  ]}
                  label={null}
                >
                  <Checkbox>{t('addCommentModal.form.check2.label')}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  required
                  name="check3"
                  valuePropName="checked"
                  className="check-input"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(t('addCommentModal.form.check3.required')),
                    },
                  ]}
                  label={null}
                >
                  <Checkbox>
                    {t('addCommentModal.form.check3.label1')}&nbsp;
                    <span
                      className="cursor-pointer underline"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open(
                          `${window.location.origin}/terms?termType=privacyPolicy`,
                          '_blank'
                        );
                      }}
                    >
                      {t('addCommentModal.form.check3.label2')}
                    </span>
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="check4"
                  valuePropName="checked"
                  label={null}
                  className="check-input"
                >
                  <Checkbox>{t('addCommentModal.form.check4.label')}</Checkbox>
                </Form.Item>
              </Col>
              {!userId && (
                <Col xs={24} className="mb-[40px] md:mb-[0px]">
                  <CaptchaMemorial
                    captchaToken={captchaToken}
                    showRequiredError={showRequiredErrorInCaptcha}
                    setCaptchaToken={(token) => {
                      showRequiredErrorInCaptcha && setShowRequiredErrorInCaptcha(false);
                      setCaptchaToken(token);
                    }}
                  />
                </Col>
              )}
            </Row>
          </Form>
        </div>
      </>
    );
  };

  const getSuccess = () => (
    <SuccessMessage
      bodyText1={t('addCommentModal.thanksForParticipating', { value: deceasedNameToShow })}
      bodyText2={t('addCommentModal.adminCheck') as string}
      buttonText={t('addCommentModal.goToMemorial')}
      onClose={() => {
        if (typeof onClose === 'function') {
          onClose();
        } else {
          navigate(-1);
        }
      }}
      title={t('addCommentModal.sentComment')}
    />
  );

  const getClassNameOfPrimaryButton = () => {
    if (userId) {
      return ((step === 0 || isMemorialCreator) && hasErrorsFormCommentAndImage) ||
        (step === 1 && hasErrorsFormPolicies)
        ? 'primaryDisabled'
        : '';
    }

    return (step === 0 && hasErrorPersonalInformation) ||
      (step === 1 && hasErrorsFormCommentAndImage) ||
      (step === 2 && (hasErrorsFormPolicies || !captchaToken))
      ? 'primaryDisabled'
      : '';
  };

  return (
    <>
      <div className="flex h-[100%] w-[100%] flex-1 flex-col md:h-auto">
        {!showSuccess && (
          <>
            <div className="flex-1 px-[16px] md:overflow-y-auto md:px-[0px]">
              <h1 className="mb-[56px] mt-[72px] hidden px-[112px] text-center md:block">
                {t('addCommentModal.addCommentToMemorial')}
              </h1>
              {!isMemorialCreator && (
                <>
                  <Steps
                    responsive={false}
                    direction="horizontal"
                    labelPlacement="vertical"
                    current={step}
                    items={stepItems}
                    className="mb-[36px] mt-[24px] md:mb-[56px] md:mt-[0px] md:px-[112px]"
                    onChange={onChangeStepInWizard}
                  />
                  <div className="mb-[120px] md:mb-[16px] md:px-[112px]">{getStepContent()}</div>
                </>
              )}
              {isMemorialCreator && (
                <div className="mt-[24px] md:mt-0 md:px-[112px]">{getCommentForm()}</div>
              )}
            </div>
            <Row className="footerButtons md:box-border md:flex md:w-[100%] md:justify-end md:p-[24px] md:shadow-up">
              {step !== 0 && (
                <Button className="md:mr-[8px]" onClick={() => setStep((prev) => prev - 1)}>
                  {t('common.back')}
                </Button>
              )}
              <Button
                htmlType="submit"
                type="primary"
                onClick={onClickPrimaryButton}
                className={`${
                  step !== 0 ? 'mb-[8px] md:mb-0' : ''
                } ${getClassNameOfPrimaryButton()}`}
                loading={saving}
              >
                {(userId && step === 1) || isMemorialCreator
                  ? t('addCommentModal.publish')
                  : t('common.next')}
              </Button>
            </Row>
          </>
        )}
        {showSuccess && <>{getSuccess()}</>}
      </div>
    </>
  );
};

export default AddCommentForm;
